import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Modal } from '../UI/Modal';
import { NotificationDropdown } from '../Dashboard/NotificationDropdown';
import { notificationService, Notification } from '../../services/notificationService';
import { NOTIFICATIONS_INBOX_UPDATED } from '../../hooks/useSignalR';
import { useAuth } from '../../contexts/AuthContext';
import { useSearch } from '../../hooks/useSearch';

import { AdminContextSwitcher } from './AdminContextSwitcher';
import UserProfileModal from './UserProfileModal';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { GlobalSearch } from './GlobalSearch';
import { DocumentNode } from '../../types/documents';
import { useAvatar } from '../../hooks/useAvatar';
import { getDisplayName } from '../../utils/nameUtils';
import { navigateNotificationActionUrl } from '../../utils/notificationActionUrl';
import { useHelpdeskHomePath } from '../../hooks/useHelpdeskHomePath';

function getInitials(name: string) {
  const names = name.split(' ').filter(n => n.length > 0);
  if (names.length === 0) return 'U';
  if (names.length === 1) return names[0][0].toUpperCase();
  return (names[0][0] + names[names.length - 1][0]).toUpperCase();
}

interface NavbarProps {
  onOpenMobileNav?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenMobileNav }) => {
  const { user, signOut } = useAuth();
  const { homePath: helpdeskHomePath } = useHelpdeskHomePath();
  const { avatarUrl } = useAvatar({ 
    userId: user?.id, 
    avatarBase64: user?.avatarBase64 
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [theme, setTheme] = useState<'claro' | 'escuro'>('claro');
  const [language, setLanguage] = useState('pt-BR');
  const [password, setPassword] = useState('');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const navigate = useNavigate();
  const isSignerProfile = user?.role?.code?.toLowerCase() === 'user';
  const userRole = user?.role?.code?.toLowerCase() ?? '';
  const canAccessSupport = user?.isAdmin || userRole === 'admin' || userRole === 'manager';

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    const onInboxUpdated = () => {
      void loadNotifications();
    };
    window.addEventListener(NOTIFICATIONS_INBOX_UPDATED, onInboxUpdated);
    return () => {
      clearInterval(interval);
      window.removeEventListener(NOTIFICATIONS_INBOX_UPDATED, onInboxUpdated);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      const [latestNotifications, count] = await Promise.all([
        notificationService.getNotifications(10),
        notificationService.getUnreadCount()
      ]);
      setNotifications(latestNotifications);
      setUnreadCount(count);
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      // Em caso de erro, manter o estado atual sem quebrar a interface
      // Não exibir toast aqui para não poluir a interface do usuário
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        if (ev.target?.result) setAvatar(ev.target.result as string);
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const formatDate = (date: string) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInMinutes = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `${diffInMinutes} minuto${diffInMinutes > 1 ? 's' : ''} atrás`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hora${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''} atrás`;
    return notificationDate.toLocaleDateString('pt-BR');
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };

  const handleSignOut = () => {
    signOut();
  };

  const handleProfileClick = () => {
    setIsProfileMenuOpen(false);
    setIsUserProfileModalOpen(true);
  };

  const handleGlobalSearchOpen = () => {
    setGlobalSearchOpen(true);
  };

  const handleGlobalSearchClose = () => {
    setGlobalSearchOpen(false);
  };

  const handleDocumentSelect = (document: DocumentNode) => {
    // Aqui você pode implementar a lógica para abrir o documento
    // Por exemplo, navegar para uma página de visualização do documento
    // navigate(`/documents/${document.id}`);
  };

  return (
    <>
      <nav className="bg-blue-600 text-white shadow-sm flex-shrink-0">
        <div className="max-w-7xl mx-0 px-2 sm:px-3 lg:px-4">
          <div className="flex justify-between h-14 md:h-16">
            <div className="flex items-center min-w-0">
              <button
                type="button"
                className="md:hidden p-2 -ml-1 rounded-md hover:bg-blue-700"
                onClick={onOpenMobileNav}
                aria-label="Abrir menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              <div className="flex-shrink-0 flex items-center pl-1 min-w-0">
                <img className="h-8 w-auto" src="/know-logo.png" alt="Logo" />
                <span className="ml-2 font-bold text-lg md:text-xl truncate hidden sm:inline">Baruk Know</span>
              </div>
              <div className="hidden md:ml-2 md:flex md:items-center">
                {user?.role?.code === 'admin' && (
                  <AdminContextSwitcher />
                )}
                {canAccessSupport && (
                  <Link
                    to={helpdeskHomePath}
                    className="ml-2 px-3 py-2 rounded-md text-sm font-medium hover:bg-blue-700 bg-blue-800"
                  >
                    Atendimento
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-3 pr-2">
              {!isSignerProfile && (
                <button
                  className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 px-2 py-2 md:px-3 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-white"
                  onClick={handleGlobalSearchOpen}
                  title="Buscar documentos"
                  type="button"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="hidden md:inline">Buscar</span>
                </button>
              )}

              {/* Copiloto: oculto para admin (menu lateral) e visitantes/signatários */}
              {user?.role?.code !== 'admin' && user?.role?.code?.toLowerCase() !== 'user' && (
                <button
                  className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 px-2 py-2 md:px-3 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-white"
                  onClick={() => navigate('/copilot')}
                  title="Abrir Copiloto"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="hidden md:inline">Copiloto</span>
                </button>
              )}

              {/* Menu Ajuda */}
              <div className="relative">
                <button
                  onClick={() => setIsHelpMenuOpen((v) => !v)}
                  className="flex items-center gap-1 px-2 py-2 md:px-3 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-white"
                  title="Ajuda"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 14h.01M16 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden md:inline">Ajuda</span>
                </button>
                {isHelpMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]">
                    <div className="py-1 text-gray-700">
                      <a
                        href="/support"
                        className="block px-4 py-2 hover:bg-gray-100"
                        onClick={() => setIsHelpMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                          </svg>
                          Suporte Técnico
                        </div>
                      </a>
                      <a
                        href="/privacy"
                        className="block px-4 py-2 hover:bg-gray-100"
                        onClick={() => setIsHelpMenuOpen(false)}
                      >
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Privacidade
                        </div>
                      </a>
                      <div className="px-4 py-2 text-xs text-gray-400 border-t mt-1">
                        <span className="font-semibold text-gray-500">Baruk</span> &copy; {new Date().getFullYear()}<br />
                        <a href="https://www.baruk.ai" target="_blank" rel="noopener noreferrer" className="hover:underline text-blue-700">www.baruk.ai</a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notificações */}
              <div className="relative">
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className="p-1 rounded-full text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
                >
                  <span className="sr-only">Ver notificações</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-blue-600" />
                  )}
                </button>

                {/* Dropdown de notificações */}
                {isNotificationsOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-[min(20rem,calc(100vw-1rem))] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]">
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="flex justify-between items-center">
                          <h3 className="text-sm font-medium text-gray-900">Notificações</h3>
                          {unreadCount > 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              onClick={() => {
                                if (notification.actionUrl)
                                {
                                  navigateNotificationActionUrl(notification.actionUrl, navigate);
                                }
                              }}
                              className={`px-4 py-3 hover:bg-gray-50 ${
                                !notification.read ? 'bg-blue-50' : ''
                              } ${notification.actionUrl ? 'cursor-pointer' : ''}`}
                            >
                              <div className="flex items-start">
                                <div className={`flex-shrink-0 ${getNotificationIcon(notification.type)}`}>
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </div>
                                <div className="ml-3 w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                                  <p className="mt-1 text-sm text-gray-500">{notification.message}</p>
                                  <p className="mt-1 text-xs text-gray-400">{formatDate(notification.createdAt)}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            Nenhuma notificação
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-2 border-t border-gray-100">
                        <Link
                          to="/notifications"
                          className="block text-sm text-center text-blue-600 hover:text-blue-800"
                          onClick={() => setIsNotificationsOpen(false)}
                        >
                          Ver todas as notificações
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Perfil do usuário */}
              <div className="relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center gap-2 max-w-xs rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white hover:bg-white hover:bg-opacity-10 px-2 py-1 rounded-md transition-colors"
                >
                  <span className="sr-only">Abrir menu do usuário</span>
                  {avatarUrl ? (
                    <img
                      className="h-8 w-8 rounded-full"
                      src={avatarUrl}
                      alt="Avatar do usuário"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-medium">
                        {getInitials(user?.name || 'U')}
                      </span>
                    </div>
                  )}
                  <span className="hidden md:inline text-white font-medium">
                    {getDisplayName(user?.name)}
                  </span>
                </button>

                {isProfileMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700 border-b">
                        <p className="font-medium">{user?.name}</p>
                        <p className="text-gray-500">{user?.email}</p>
                      </div>
                      <button
                        onClick={handleProfileClick}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Seu perfil
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Sair
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Drawer/Modal do Copiloto */}
      {copilotOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="w-full max-w-lg bg-white h-full shadow-lg p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-blue-700">Copiloto</h2>
              <div className="flex gap-2">
                <button
                  className="text-gray-500 hover:text-blue-700"
                  title="Expandir Copiloto"
                  onClick={() => { setCopilotOpen(false); navigate('/copilot'); }}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                  </svg>
                </button>
                <button onClick={() => setCopilotOpen(false)} className="text-gray-500 hover:text-blue-700">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Copiloto</h3>
                <p className="text-gray-500 mb-4">
                  Acesse o chat completo através do menu lateral ou clique em "Expandir Copiloto".
                </p>
                <button
                  onClick={() => { setCopilotOpen(false); navigate('/copilot'); }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  🚀 Abrir Chat Completo
                </button>
              </div>
            </div>
          </div>
          <div className="flex-1 bg-black bg-opacity-30" onClick={() => setCopilotOpen(false)} />
        </div>
      )}

      {/* Modal do Perfil do Usuário */}
      <UserProfileModal
        isOpen={isUserProfileModalOpen}
        onClose={() => setIsUserProfileModalOpen(false)}
      />

      {!isSignerProfile && (
        <GlobalSearch
          isOpen={globalSearchOpen}
          onClose={handleGlobalSearchClose}
          onDocumentSelect={handleDocumentSelect}
        />
      )}
    </>
  );
}; 
